/**
 * Search, as a component rather than a screen.
 *
 * It was `pages/Search.tsx` and nothing else, which meant searching was a navigation: the
 * screen you were browsing went away, and coming back from an empty result meant Back and
 * then finding your place again. Extracting the panel lets the same thing be a route — for
 * the television, for a deep link, and for the phone's tab bar, where a tab that opens an
 * overlay instead of a page is a lie about where you are — and, on a desktop, an overlay
 * over whatever you were already looking at.
 *
 * One component, two mounts. The alternative is two search implementations that drift.
 *
 * ## Three lists, never one
 *
 * The endpoint answers with live, movies and series as separate arrays, and this keeps them
 * separate. Merging them would undo the rule the whole catalogue is built on — a channel
 * and a film are different things to press play on, and a mixed result set makes the viewer
 * read every row to work out which is which.
 *
 * ## Debounced, not submitted
 *
 * Typing searches. A search button would be one more press on every query, and on a
 * television one more focus stop between the keyboard and the results.
 */
import { useEffect, useRef, useState } from 'react';
import type { CatalogFacetDto, CatalogFacetsDto, CatalogItemDto } from '@filmodasi/shared';
import { api } from '../api/client';
import { useApi } from '../api/hooks';
import { Empty, ErrorBox, ItemCard, SkeletonGrid } from './ui';
import { Icon } from './Icon';
import { VoiceIndicator, type VoiceStep } from './VoiceIndicator';
import { useVoiceSearch } from '../voice/useVoiceSearch';
import { forgetSearch, recentSearches, rememberSearch } from '../search/recentSearches';

interface SearchResults {
  live: CatalogItemDto[];
  movies: CatalogItemDto[];
  series: CatalogItemDto[];
}

/**
 * Long enough that a typed word is one request, short enough to feel immediate.
 *
 * The floor is not arbitrary: below roughly 200 ms a fast typist still generates a request
 * per keystroke, and each one is a query against a 150k-document collection.
 */
const DEBOUNCE_MS = 280;

/** Below this, a query matches so much that the results are noise. */
const MIN_QUERY = 2;

/**
 * How long "işte bunu duydum" stays on screen before the search takes over.
 *
 * Short enough not to be a delay, long enough to read four or five words — which is the
 * point: if the recogniser misheard, this is the moment somebody notices, rather than
 * after a page of wrong results.
 */
const HEARD_MS = 1400;

export function SearchPanel({ onNavigate }: { onNavigate?: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recents, setRecents] = useState<string[]>(recentSearches);
  const [heard, setHeard] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  /*
   * One request, on mount, for the whole chip strip.
   *
   * `useApi` and not a hand-rolled fetch, so a failure is simply "no chips" rather than an
   * error on a screen that is otherwise working perfectly — the field beneath them is the
   * feature, and these are a convenience above it.
   */
  const facets = useApi<CatalogFacetsDto>('/api/catalog/facets');

  const voice = useVoiceSearch({
    onResult: (text) => {
      setHeard(text);
      setQuery(text);
      inputRef.current?.focus();
    },
  });

  /* The recognised phrase is a confirmation, not a state to sit in. */
  useEffect(() => {
    if (!heard) return;
    const timer = window.setTimeout(() => setHeard(''), HEARD_MS);
    return () => window.clearTimeout(timer);
  }, [heard]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      setResults(null);
      setError(null);
      return;
    }

    /*
     * Both a timer and a cancellation flag.
     *
     * The timer collapses keystrokes into one request. The flag handles the race the timer
     * cannot: a request already in flight when the query changes will still resolve, and
     * without this its results would overwrite the newer ones — the classic search bug
     * where the answer to what you typed three letters ago wins because it was slower.
     */
    let cancelled = false;
    setLoading(true);

    const timer = window.setTimeout(() => {
      api<SearchResults>(`/api/catalog?q=${encodeURIComponent(trimmed)}&limit=24`)
        .then((data) => {
          if (cancelled) return;
          setResults(data);
          setError(null);
          /*
           * Remembered on a *result*, not on a keystroke.
           *
           * Recording every intermediate query would fill the list with "b", "ba", "bat"
           * — the prefixes of one search, none of which anybody typed on purpose. Waiting
           * for the response also means a query that found nothing is still remembered,
           * which is right: "it found nothing" is exactly the search worth retrying later,
           * once the catalogue has resynced.
           */
          setRecents(rememberSearch(trimmed));
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Arama başarısız');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const trimmed = query.trim();
  const total = results ? results.live.length + results.movies.length + results.series.length : 0;

  const step: VoiceStep = voice.error
    ? 'error'
    : voice.listening
      ? 'listening'
      : heard
        ? 'heard'
        : 'idle';

  return (
    <div className="search">
      <div className="search__bar">
        <div className="search__field">
          <span className="search__icon" aria-hidden>
            <Icon name="search" size={1.1} />
          </span>
          <input
            ref={inputRef}
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Film, dizi, kanal, oyuncu veya tür ara"
            aria-label="Ara"
          />
          {query && (
            <button
              type="button"
              className="search__clear"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              aria-label="Aramayı temizle"
            >
              <Icon name="close" size={1} />
            </button>
          )}
        </div>
        {/* Absent where the device cannot listen, rather than present and inert. */}
        {voice.supported && (
          <button
            type="button"
            className={`search__mic${voice.listening ? ' is-listening' : ''}`}
            onClick={voice.listening ? voice.stop : voice.start}
            aria-label={voice.listening ? 'Dinlemeyi durdur' : 'Sesle ara'}
            aria-pressed={voice.listening}
          >
            <Icon name="mic" size={1.05} />
            <span className="search__micLabel">{voice.listening ? 'Dinliyor' : 'Sesle ara'}</span>
          </button>
        )}
      </div>

      <VoiceIndicator step={step} interim={voice.interim} heard={heard} message={voice.error} />

      {error && <ErrorBox message={error} />}

      {trimmed.length > 0 && trimmed.length < MIN_QUERY && (
        <p className="search__hint">En az {MIN_QUERY} harf yazın.</p>
      )}

      {/*
        The shape of the answer while it is on its way.

        A grid of tiles rather than a spinner, for the same reason as everywhere else: the
        results are a grid, and replacing a spinner with one relays the whole panel. Only
        for the first query — a skeleton under existing results reads as the results having
        been thrown away.
      */}
      {loading && !results && (
        <>
          <p className="sr-only" role="status">
            Aranıyor…
          </p>
          <SkeletonGrid count={8} />
        </>
      )}

      {results && total === 0 && !loading && (
        /* The advice is not filler: the search matches cast and genre as well as titles,
           and nobody guesses that from an empty page. */
        <Empty title={`“${trimmed}” bulunamadı`}>
          Oyuncu adıyla ya da tür adıyla da arayabilirsiniz — “komedi”, “Tom Hanks”.
        </Empty>
      )}

      {results && (
        <>
          <ResultGroup title="Filmler" items={results.movies} onNavigate={onNavigate} />
          <ResultGroup title="Diziler" items={results.series} onNavigate={onNavigate} />
          <ResultGroup title="Canlı TV" items={results.live} onNavigate={onNavigate} />
        </>
      )}

      {!results && !loading && trimmed.length === 0 && (
        <div className="search__idle">
          {/*
            What this catalogue actually holds, measured server-side.

            The chips set the *query*, not a filter, and that is deliberate rather than a
            shortcut: the search endpoint already matches genre prose — the empty state
            below has been telling people to type "komedi" since the screen existed — so a
            chip is the same search anyone could type, spelled for them. Wiring it to the
            grid's genre filter instead would mean a chip and the identical typed word did
            two different things.
          */}
          {facets.data && (
            <>
              <FacetChips
                title="Türler"
                facets={facets.data.genres}
                onPick={(label) => setQuery(label)}
              />
              <FacetChips
                title="Diller"
                facets={facets.data.languages}
                onPick={(label) => setQuery(label)}
              />
            </>
          )}

          {recents.length > 0 && (
            <section className="search__recents">
              <h2 className="search__recentsTitle">Son aramalar</h2>
              <ul className="search__chips">
                {recents.map((q) => (
                  <li key={q} className="search__chip">
                    {/* Two targets in one chip: the word searches, the × forgets. Splitting
                        them is what makes the list editable without a settings screen. */}
                    <button type="button" className="search__chipGo" onClick={() => setQuery(q)}>
                      {q}
                    </button>
                    <button
                      type="button"
                      className="search__chipDrop"
                      onClick={() => setRecents(forgetSearch(q))}
                      aria-label={`“${q}” aramasını listeden kaldır`}
                    >
                      <Icon name="close" size={0.85} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className="search__hint">
            Bir isim yazın. Oyuncu adları, zenginleştirilmiş filmlerde aranabilir.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * One strip of suggestion chips.
 *
 * Renders nothing when the group is empty, which is the ordinary case on a fresh account:
 * the facets are measured, and a catalogue that has not finished syncing genuinely has no
 * genre worth suggesting. A heading over an empty strip would read as a broken screen.
 *
 * The count is not drawn. It is the reason the server dropped everything under eight
 * results — a chip leading to three is worse than no chip — but printing "Aksiyon 412"
 * turns a row of suggestions into a table of statistics.
 */
function FacetChips({
  title,
  facets,
  onPick,
}: {
  title: string;
  facets: CatalogFacetDto[];
  onPick: (label: string) => void;
}) {
  if (facets.length === 0) return null;
  return (
    <section className="search__facets">
      <h2 className="search__recentsTitle">{title}</h2>
      <ul className="search__chips">
        {facets.map((f) => (
          <li key={f.value} className="search__chip">
            <button
              type="button"
              className="search__chipGo search__chipGo--only"
              onClick={() => onPick(f.label)}
            >
              {f.label}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ResultGroup({
  title,
  items,
  onNavigate,
}: {
  title: string;
  items: CatalogItemDto[];
  onNavigate?: () => void;
}) {
  // An empty group is not rendered at all. A heading with nothing under it reads as a
  // failure, and three of them read as a broken screen.
  if (items.length === 0) return null;
  return (
    <section className="search__group">
      <h2>
        {title} <span className="search__count">{items.length}</span>
      </h2>
      {/*
        `onNavigate` closes the overlay when a result is opened.

        Bubbled from the container rather than threaded through `ItemCard`, which would mean
        every grid in the app carrying a prop that only search uses. A click anywhere in the
        group that reaches a link is a navigation; nothing else in here is clickable.
      */}
      <div className="grid" onClick={onNavigate}>
        {items.map((item) => (
          <ItemCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}
